import { useEffect, useMemo, useState } from "react";

/**
 * ParticleBurst — radial burst of colorful particles from a center point.
 *
 * Pure CSS animation (GPU accelerated). Auto-cleans up after `duration`.
 * Uses a fixed-position overlay so it can be rendered anywhere; the burst
 * originates from screen center by default, but can be repositioned via
 * `originX`/`originY` (% values).
 *
 * Props:
 *   count    — number of particles (default 80)
 *   duration — total burst time in ms (default 1800)
 *   colors   — array of CSS color strings; particles cycle through them
 *   originX  — horizontal origin (default '50%')
 *   originY  — vertical origin (default '50%')
 *   onDone   — callback fired when animation finishes
 */
const DEFAULT_COLORS = [
  "#0073ea", // Maidly blue
  "#00a3ff", // bright sky
  "#fbbf24", // amber
  "#10b981", // emerald
  "#f472b6", // pink
  "#8b5cf6", // violet
  "#ffffff", // white
];

const ParticleBurst = ({
  count = 80,
  duration = 1800,
  colors = DEFAULT_COLORS,
  originX = "50%",
  originY = "50%",
  onDone,
}) => {
  const [visible, setVisible] = useState(true);

  const particles = useMemo(() => {
    return Array.from({ length: count }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2; // radians
      const distance = 120 + Math.random() * 320; // px outward
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance;
      const size = 6 + Math.random() * 8; // px
      const delay = Math.random() * 120; // ms — slight stagger
      const lifetime = duration - delay;
      const color = colors[i % colors.length];
      const shape = Math.random() > 0.5 ? "circle" : "star";
      const rotation = Math.random() * 720;
      return { id: i, dx, dy, size, delay, lifetime, color, shape, rotation };
    });
  }, [count, duration, colors]);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(false);
      if (onDone) onDone();
    }, duration + 200);
    return () => clearTimeout(t);
  }, [duration, onDone]);

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes particle-burst {
          0% {
            transform: translate(-50%, -50%) translate(0, 0) scale(1) rotate(0deg);
            opacity: 1;
          }
          80% { opacity: 1; }
          100% {
            transform: translate(-50%, -50%) translate(var(--dx), var(--dy)) scale(0.4) rotate(var(--rot));
            opacity: 0;
          }
        }
        .particle-burst-particle {
          position: absolute;
          left: 0;
          top: 0;
          will-change: transform, opacity;
          pointer-events: none;
          animation-name: particle-burst;
          animation-fill-mode: forwards;
          animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
        }
        @media (prefers-reduced-motion: reduce) {
          .particle-burst-particle { animation-duration: 0ms !important; opacity: 0 !important; }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 9999,
          overflow: "hidden",
        }}
      >
        {particles.map((p) => {
          const isStar = p.shape === "star";
          return (
            <span
              key={p.id}
              className="particle-burst-particle"
              style={{
                left: originX,
                top: originY,
                width: isStar ? "auto" : `${p.size}px`,
                height: isStar ? "auto" : `${p.size}px`,
                fontSize: isStar ? `${p.size * 1.6}px` : undefined,
                background: isStar ? "transparent" : p.color,
                color: p.color,
                borderRadius: isStar ? 0 : "50%",
                boxShadow: isStar ? "none" : `0 0 ${p.size}px ${p.color}`,
                lineHeight: 1,
                animationDuration: `${p.lifetime}ms`,
                animationDelay: `${p.delay}ms`,
                "--dx": `${p.dx}px`,
                "--dy": `${p.dy}px`,
                "--rot": `${p.rotation}deg`,
              }}
            >
              {isStar ? "✦" : ""}
            </span>
          );
        })}
      </div>
    </>
  );
};

export default ParticleBurst;
